/**
 * What an organization's agents spent, added up from its trail of runs — and
 * the monthly budgets an owner or an administrator sets against it. The shapes
 * the account area's Spend page reads and writes, and the signal an install is
 * handed back when a budget its member's runs fall under is near or past.
 *
 * Spend is the runs' **estimated** cost at API list prices
 * (`@airlock/contracts/usage/prices`), whatever the session was billed through:
 * a subscription pays nothing per token, and the figure is still the honest way
 * to compare a team's month with another's. A run counts in the month it
 * started in.
 */

/** What a period's spend can be broken down by. */
export const SPEND_GROUPS = ['member', 'team', 'workspace', 'agent', 'model'] as const;
export type SpendGroup = (typeof SPEND_GROUPS)[number];

export const SPEND_GROUP_WORDS: Record<SpendGroup, string> = {
  member: 'Member',
  team: 'Team',
  workspace: 'Workspace',
  agent: 'Agent',
  model: 'Model'
};

export interface SpendFigures {
  runs: number;
  tokens: number;
  costMicros: number;
}

export interface SpendLine extends SpendFigures {
  /** The member's email, the team's or workspace's id, the agent or model id; `''` for members in no team. */
  key: string;
  label: string;
}

/** `GET /api/orgs/:org/spend?from=&to=&by=`. */
export interface SpendAnswer {
  from: string;
  to: string;
  by: SpendGroup;
  total: SpendFigures;
  /** Largest first. A member in two teams counts in each, so team lines may add up to more than the total. */
  lines: SpendLine[];
  /** One entry per day of the period, empty days included, oldest first. */
  days: { day: string; costMicros: number }[];
  /** Every budget of the organization, as it stands this month. */
  budgets: BudgetStatus[];
}

/* ── budgets ──────────────────────────────────────────────────────────────── */

export const BUDGET_SCOPES = ['org', 'team', 'workspace'] as const;
export type BudgetScope = (typeof BUDGET_SCOPES)[number];

/** The fractions of a budget a warning is sent at — once each, per budget and month. */
export const BUDGET_THRESHOLDS = [80, 100] as const;

/** The most a monthly budget may be, in dollars — a typo with three more zeros is refused, not kept. */
export const MAX_BUDGET_USD = 1_000_000;

export interface Budget {
  id: string;
  scope: BudgetScope;
  /** The team's or the workspace's id; `null` for the organization's own. */
  scopeId: string | null;
  /** The organization's, a team's or a workspace's name, as the page and the mail say it. */
  label: string;
  monthlyMicros: number;
}

export type BudgetState = 'ok' | 'near' | 'over';

export interface BudgetStatus extends Budget {
  /** The UTC month it is read for, `YYYY-MM`. */
  month: string;
  spentMicros: number;
  /** Whole percent of the budget spent, uncapped. */
  percent: number;
  state: BudgetState;
}

/** `PUT /api/orgs/:org/budgets` — one budget set, or replaced, by its scope. */
export interface BudgetRequest {
  scope: BudgetScope;
  scopeId: string | null;
  monthlyUsd: number;
}

/**
 * Whether the organization's spending is near or past one of its monthly
 * budgets, said back to the install that sent runs so it can say so to its
 * member. A soft signal: nothing on either side stops a run because of it.
 */
export interface BudgetSignal {
  state: 'near' | 'over';
  /** The budget's own words: the organization, a team, a workspace. */
  scope: string;
  month: string;
  percent: number;
}

/** A budget's state at a spend: near from 80 %, over from 100 %. A budget of nothing is over at the first cent. */
export function budgetState(spentMicros: number, monthlyMicros: number): { percent: number; state: BudgetState } {
  if (monthlyMicros <= 0) return { percent: spentMicros > 0 ? 100 : 0, state: spentMicros > 0 ? 'over' : 'ok' };
  const percent = Math.floor((spentMicros * 100) / monthlyMicros);
  return { percent, state: percent >= 100 ? 'over' : percent >= 80 ? 'near' : 'ok' };
}

/** The UTC month an instant falls in, `YYYY-MM`. */
export function monthOf(at: number | string | Date): string {
  return new Date(at).toISOString().slice(0, 7);
}

/** The first instant of a month and of the one after it, ISO — the half-open range a month's runs started in. */
export function monthRange(month: string): { from: string; until: string } {
  const [year, mon] = month.split('-').map(Number);
  return {
    from: new Date(Date.UTC(year, mon - 1, 1)).toISOString(),
    until: new Date(Date.UTC(year, mon, 1)).toISOString()
  };
}
